import { Element } from "src/types/element";

import { WidgetPropertyDefinition } from "../Widget.typings";

export const getElementByPath = (
  elements: Element[],
  elementPath: string
): Element | Element["elements"][number] | undefined => {
  const elementCodes = elementPath.split(".");
  let currentElement: Element | Element["elements"][number] | undefined;

  for (const elementCode of elementCodes) {
    // Always prefer an exact match on schemaElement.code when possible,
    // then fall back to typeCode, then configurableType.code.
    const searchList =
      currentElement && "elements" in currentElement
        ? currentElement.elements
        : !currentElement
          ? elements
          : undefined;

    if (!searchList) {
      return undefined;
    }

    // When all siblings have schemaElement, match only by schemaElement.code
    // so we don't pick another variant (e.g. lucy_telephone) when the requested
    // one (e.g. ControleTelephoneLucy) is missing.
    const allHaveSchemaElement = searchList.every((el) => el.schemaElement != null);
    const match = allHaveSchemaElement
      ? searchList.find((el) => el.schemaElement?.code === elementCode)
      : (searchList.find((el) => el.schemaElement?.code === elementCode) ??
        searchList.find((el) => el.typeCode === elementCode) ??
        searchList.find((el) => el.configurableType.code === elementCode));

    if (!match) {
      return undefined;
    }

    currentElement = match as Element | Element["elements"][number];
  }

  return currentElement;
};

export const findTargetElement = (
  elements: Element[] | undefined,
  elementPath: string | undefined
): Element | Element["elements"][number] | undefined => {
  if (!elementPath) return undefined;

  if (elements && elementPath.includes(".")) {
    return getElementByPath(elements, elementPath);
  }

  return elements?.find(
    (element) => element.typeCode === elementPath || element.configurableType.code === elementPath
  );
};

export const isCodeEqual = (a?: string, b?: string) => a?.toLowerCase() === b?.toLowerCase();

export type DisplayedPropertyValidation = {
  isValid: boolean;
  propertyDefinition: WidgetPropertyDefinition;
  hasMultipleProperties: boolean;
  hasOneProperty: boolean;
};

export const isDisplayedPropertyValid = (
  currentDisplayedProperty: WidgetPropertyDefinition,
  propertyDefinitions?: WidgetPropertyDefinition[]
): DisplayedPropertyValidation => {
  const hasCode =
    currentDisplayedProperty.code !== undefined && currentDisplayedProperty.code !== null;

  const propertyDefinition = hasCode
    ? (propertyDefinitions?.find(
        (propertyDefinition) => propertyDefinition.code === currentDisplayedProperty.code
      ) ?? currentDisplayedProperty)
    : currentDisplayedProperty;

  const hasMultipleProperties =
    Array.isArray(propertyDefinition.properties) && propertyDefinition.properties.length > 0;

  const hasOneProperty =
    propertyDefinition.property !== undefined && propertyDefinition.property !== null;

  return {
    isValid: hasMultipleProperties || hasOneProperty || hasCode,
    propertyDefinition,
    hasMultipleProperties,
    hasOneProperty,
  };
};
